/** Desktop session exchange/refresh/signout and admin BFF login (spec section 2). */
import { z } from 'zod';
import { DeviceIdSchema } from '../ids.js';
import { CapabilitySchema } from '../roles.js';
import {
  CompactJwsSchema,
  EmptyRequestSchema,
  IsoDateTimeSchema,
  JwksSchema,
  NoQuerySchema,
  OkSchema,
  RedirectResponseSchema,
  SemVerSchema,
  defineRoute,
} from './common.js';
import { DeviceSchema, SessionUserSchema } from './entities.js';
// real-google change
import { HostedDomainSchema } from './integrations.js';

// real-google change: who performs the PKCE code exchange for the desktop sign-in.
export const DESKTOP_EXCHANGE_MODES = ['server', 'desktop'] as const;
export const DesktopExchangeModeSchema = z.enum(DESKTOP_EXCHANGE_MODES);
export type DesktopExchangeMode = z.infer<typeof DesktopExchangeModeSchema>;

export const DesktopAuthConfigSchema = z.object({
  issuer: z.string().url(),
  clientId: z.string(),
  scopes: z.array(z.string()),
  /** OIDC discovery document URL. */
  discoveryUrl: z.string().url(),
  /** Loopback redirect pattern the server accepts, e.g. http://127.0.0.1:{port}/callback */
  redirectUriTemplate: z.string(),
  mode: z.enum(['local', 'cloud']),
  // real-google change
  /**
   * `desktop`: the desktop exchanges the code at the IdP token endpoint itself and posts the ID
   * token (local IdP). `server`: the desktop posts `{code, codeVerifier, redirectUri}` and the
   * server exchanges it with the desktop client secret it holds (Google Workspace sign-in, whose
   * Desktop clients require the secret at the token endpoint).
   */
  exchangeMode: DesktopExchangeModeSchema,
  /** Workspace hosted domain the IdP must assert (`hd`); null for the local IdP. */
  hostedDomain: HostedDomainSchema.nullable(),
});
export type DesktopAuthConfig = z.infer<typeof DesktopAuthConfigSchema>;

const desktopExchangeCommonShape = {
  /**
   * Nonce the desktop generated for this login attempt; the server requires it and rejects a
   * token whose `nonce` claim differs. (changed by server: required, was optional)
   */
  nonce: z.string().min(8).max(256),
  deviceId: DeviceIdSchema.optional(),
  appVersion: SemVerSchema,
};

/** Exchange with an ID token the desktop already obtained (exchangeMode `desktop`). */
export const DesktopExchangeIdTokenRequestSchema = z
  .object({
    /** IdP ID token (EdDSA/RS256 compact JWS) obtained via PKCE in the system browser. */
    idToken: CompactJwsSchema,
    ...desktopExchangeCommonShape,
  })
  .strict();
export type DesktopExchangeIdTokenRequest = z.infer<typeof DesktopExchangeIdTokenRequestSchema>;

// real-google change
/** PKCE code verifier (RFC 7636 section 4.1: 43 to 128 unreserved characters). */
export const PkceCodeVerifierSchema = z.string().min(43).max(128).regex(/^[A-Za-z0-9._~-]+$/, 'expected a PKCE code verifier');
/** Loopback redirect the desktop listened on for this attempt (native-app rule: http, 127.0.0.1 / [::1] / localhost, any port). */
export const LoopbackRedirectUriSchema = z
  .string()
  .max(512)
  .regex(/^http:\/\/(?:127\.0\.0\.1|\[::1\]|localhost)(?::\d{1,5})?\/[^\s?#]*$/, 'expected a loopback redirect URI');

/**
 * Exchange with the authorization code (exchangeMode `server`): the server redeems the code at the
 * IdP token endpoint with the desktop client secret it holds, verifies the ID token (issuer,
 * audience, nonce, `hd`, `email_verified`) and issues the Agency session. Identity only; no
 * customer data passes through.
 */
export const DesktopExchangeCodeRequestSchema = z
  .object({
    code: z.string().min(1).max(2048),
    codeVerifier: PkceCodeVerifierSchema,
    redirectUri: LoopbackRedirectUriSchema,
    ...desktopExchangeCommonShape,
  })
  .strict();
export type DesktopExchangeCodeRequest = z.infer<typeof DesktopExchangeCodeRequestSchema>;

/** Either form; both objects are strict, so the presence of `idToken` or `code` selects the branch. */
export const DesktopExchangeRequestSchema = z.union([DesktopExchangeIdTokenRequestSchema, DesktopExchangeCodeRequestSchema]);
export type DesktopExchangeRequest = z.infer<typeof DesktopExchangeRequestSchema>;

export function isDesktopCodeExchange(request: DesktopExchangeRequest): request is DesktopExchangeCodeRequest {
  return 'code' in request;
}

export const DesktopExchangeResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresAt: IsoDateTimeSchema,
  user: SessionUserSchema,
  /** Present when deviceId was supplied and is still registered to this user. */
  device: DeviceSchema.nullable(),
  /** True when the server requires a newer app before issuing an entitlement. */
  updateRequired: z.boolean(),
  minAppVersion: SemVerSchema,
});
export type DesktopExchangeResponse = z.infer<typeof DesktopExchangeResponseSchema>;

export const DesktopRefreshRequestSchema = z.object({ refreshToken: z.string().min(1) }).strict();
export const DesktopRefreshResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresAt: IsoDateTimeSchema,
});
export type DesktopRefreshResponse = z.infer<typeof DesktopRefreshResponseSchema>;

export const DesktopSessionResponseSchema = z.object({
  user: SessionUserSchema,
  device: DeviceSchema.nullable(),
  capabilities: z.array(CapabilitySchema),
  expiresAt: IsoDateTimeSchema,
  serverTime: IsoDateTimeSchema,
});
export type DesktopSessionResponse = z.infer<typeof DesktopSessionResponseSchema>;

export const AdminLoginQuerySchema = z.object({
  /** Admin SPA path to return to after login (same-origin path only). */
  returnTo: z.string().regex(/^\/[^\s]*$/).max(512).optional(),
});
export const AdminCallbackQuerySchema = z
  .object({
    /** Absent on an IdP error redirect (`error` is set instead). (changed by server: optional) */
    code: z.string().min(1).optional(),
    state: z.string().min(1),
    error: z.string().max(200).optional(),
    error_description: z.string().max(1000).optional(),
  })
  .refine((q) => q.code !== undefined || q.error !== undefined, { message: 'code or error is required', path: ['code'] });

// added by server: the admin BFF login never renders JSON to the browser; every failure of
// `admin.auth.callback` and `admin.auth.invite` redirects to `${adminOrigin}/login?error=<reason>`
// with one of these stable codes. The admin Login page maps each to copy; unknown codes fall back
// to a generic message. Codes are never removed or renamed; new ones are appended.
export const ADMIN_LOGIN_ERROR_REASONS = [
  /** The login flow expired, was already used, or the callback came from a browser that did not start it. Start again. */
  'state_invalid',
  /** The identity provider returned an error (or no code) on the callback. */
  'idp_error',
  /** The authorization code could not be exchanged or the ID token failed verification (issuer, audience, nonce, signature). */
  'token_invalid',
  /** The authenticated identity is not an invited Agency user. */
  'user_not_invited',
  /** The user exists but is suspended. */
  'user_suspended',
  /**
   * The invitation token is unknown or already consumed, or the invited user is no longer in the
   * invited state. Consumed tokens (accepted, cancelled, swept as expired) drop their hash for
   * single-use hygiene, so a re-opened link lands here rather than on the two status codes below.
   */
  'invitation_invalid',
  /** The invitation passed its expiry (still pending, not yet swept). */
  'invitation_expired',
  /** Reserved: the invitation was already accepted; sign in normally. */
  'invitation_accepted',
  /** Reserved: the invitation was cancelled by an administrator. */
  'invitation_cancelled',
  /** The invitation exists but the authenticated identity (email or subject) does not match the invitee. */
  'identity_mismatch',
] as const;
export const AdminLoginErrorReasonSchema = z.enum(ADMIN_LOGIN_ERROR_REASONS);
export type AdminLoginErrorReason = z.infer<typeof AdminLoginErrorReasonSchema>;

// added by server: invitation redemption entry point (mailed link) that starts the admin BFF login
// with the token bound to the flow; the callback accepts the invitation with the authenticated subject.
export const AdminInviteParamsSchema = z.object({ token: z.string().min(16).max(256) });

export const AdminSessionResponseSchema = z.object({
  user: SessionUserSchema,
  capabilities: z.array(CapabilitySchema),
  recentAuthAt: IsoDateTimeSchema,
  /** True when recentAuthAt is within RECENT_AUTH_WINDOW_MS. */
  recentAuthFresh: z.boolean(),
  expiresAt: IsoDateTimeSchema,
  /** Double-submit CSRF token; send as x-csrf-token on mutating requests. */
  csrfToken: z.string(),
  serverTime: IsoDateTimeSchema,
  mode: z.enum(['local', 'cloud']),
});
export type AdminSessionResponse = z.infer<typeof AdminSessionResponseSchema>;

export const HealthResponseSchema = z.object({
  ok: z.boolean(),
  version: z.string(),
  mode: z.enum(['local', 'cloud']),
  db: z.enum(['pglite', 'pg']),
  time: IsoDateTimeSchema.optional(),
});
export type HealthResponse = z.infer<typeof HealthResponseSchema>;

export const authRoutes = {
  'health.get': defineRoute({
    id: 'health.get',
    method: 'GET',
    path: '/health',
    auth: 'public',
    requestSchema: NoQuerySchema,
    responseSchema: HealthResponseSchema,
    description: 'Liveness check with mode and database kind.',
  }),
  'jwks.get': defineRoute({
    id: 'jwks.get',
    method: 'GET',
    path: '/.well-known/jwks.json',
    auth: 'public',
    requestSchema: NoQuerySchema,
    responseSchema: JwksSchema,
    description: 'Entitlement signing public keys.',
  }),
  'auth.desktop.config': defineRoute({
    id: 'auth.desktop.config',
    method: 'GET',
    path: '/v1/auth/desktop/config',
    auth: 'public',
    requestSchema: NoQuerySchema,
    responseSchema: DesktopAuthConfigSchema,
    description: 'OIDC client configuration for the desktop PKCE flow.',
  }),
  'auth.desktop.exchange': defineRoute({
    id: 'auth.desktop.exchange',
    method: 'POST',
    path: '/v1/auth/desktop/exchange',
    auth: 'public',
    requestSchema: DesktopExchangeRequestSchema,
    responseSchema: DesktopExchangeResponseSchema,
    noBodyLog: true,
    rateLimit: { max: 10, windowSeconds: 60 },
    description: 'Exchange a verified IdP ID token for a desktop session. Denies uninvited or suspended users.',
  }),
  'auth.desktop.refresh': defineRoute({
    id: 'auth.desktop.refresh',
    method: 'POST',
    path: '/v1/auth/desktop/refresh',
    auth: 'public',
    requestSchema: DesktopRefreshRequestSchema,
    responseSchema: DesktopRefreshResponseSchema,
    noBodyLog: true,
    description: 'Rotate the refresh token and issue a new access token.',
  }),
  'auth.desktop.signout': defineRoute({
    id: 'auth.desktop.signout',
    method: 'POST',
    path: '/v1/auth/desktop/signout',
    auth: 'desktop',
    requestSchema: EmptyRequestSchema,
    responseSchema: OkSchema,
    description: 'Revoke the current desktop session and refresh token.',
  }),
  'auth.desktop.session': defineRoute({
    id: 'auth.desktop.session',
    method: 'GET',
    path: '/v1/auth/session',
    auth: 'desktop',
    requestSchema: NoQuerySchema,
    responseSchema: DesktopSessionResponseSchema,
    description: 'Current desktop session, user and capabilities.',
  }),
  'admin.auth.login': defineRoute({
    id: 'admin.auth.login',
    method: 'GET',
    path: '/admin/auth/login',
    auth: 'public',
    requestSchema: AdminLoginQuerySchema,
    responseSchema: RedirectResponseSchema,
    description: 'Start the admin BFF login: redirects to the IdP authorize endpoint with PKCE + state.',
  }),
  'admin.auth.callback': defineRoute({
    id: 'admin.auth.callback',
    method: 'GET',
    path: '/admin/auth/callback',
    auth: 'public',
    requestSchema: AdminCallbackQuerySchema,
    responseSchema: RedirectResponseSchema,
    description: 'IdP redirect target: exchanges the code, sets the HttpOnly session cookie, redirects to the SPA.',
  }),
  // added by server
  'admin.auth.invite': defineRoute({
    id: 'admin.auth.invite',
    method: 'GET',
    path: '/admin/auth/invite/:token',
    auth: 'public',
    paramsSchema: AdminInviteParamsSchema,
    requestSchema: NoQuerySchema,
    responseSchema: RedirectResponseSchema,
    rateLimit: { max: 30, windowSeconds: 60 },
    description: 'Invitation link target: binds the single-use token to a login flow and redirects to the IdP; the callback accepts the invitation for the authenticated subject.',
  }),
  'admin.auth.session': defineRoute({
    id: 'admin.auth.session',
    method: 'GET',
    path: '/admin/auth/session',
    auth: 'admin',
    requestSchema: NoQuerySchema,
    responseSchema: AdminSessionResponseSchema,
    description: 'Current admin session with CSRF token and recent-auth freshness.',
  }),
  'admin.auth.logout': defineRoute({
    id: 'admin.auth.logout',
    method: 'POST',
    path: '/admin/auth/logout',
    auth: 'admin',
    requestSchema: EmptyRequestSchema,
    responseSchema: OkSchema,
    description: 'Clear the admin session cookie and revoke the session.',
  }),
} as const;
